export const content = {
  de: `
# Von kostenlosem Skool-Member zu Hochpreiskunden: Der systematische Prozess

Du hast eine Skool-Community mit kostenlosen Mitgliedern – aber wie konvertierst Du sie systematisch zu zahlenden Hochpreiskunden?

In diesem Artikel zeige ich Dir den exakten 3-Phasen-Prozess, mit dem Du:

- Kostenlose Members systematisch aufwärmst
- Ohne aufdringlich zu wirken verkaufst
- Hochpreiskunden gewinnst
- Alles automatisiert und skalierbar machst

Nach Dutzenden Skool-Community-Setups ist dies der bewährte Prozess, der funktioniert.

## Das Problem mit Skool-Communities

### Was die meisten falsch machen:

**Szenario 1: Keine Strategie**

- Community aufbauen
- Leute reinbringen
- Hoffen, dass sie kaufen
- **Ergebnis:** Niemand kauft

**Szenario 2: Zu aggressiv**

- Community aufbauen
- Sofort pitchen
- Ständig verkaufen
- **Ergebnis:** Leute verlassen Community

**Szenario 3: Kein System**

- Community aufbauen
- Manuell Leute anschreiben
- Chaotisch
- **Ergebnis:** Ineffizient, nicht skalierbar

### Die Lösung: Ein systematischer Prozess

**Was Du brauchst:**

- **Klare Phasen** – Wann passiert was?
- **Automatisierung** – Kein manuelles Chaos
- **CRM-Integration** – Alles getrackt
- **Balance** – Wert geben + Verkaufen

Das ist der Schlüssel zum Erfolg.

## Der 3-Phasen-Prozess: Übersicht

### Phase 1: Willkommen (Tag 0)

**Was passiert:**

- Automatische Willkommensnachricht vom Coach
- Erstkontakt vom Setter (innerhalb 24h)
- Beziehung aufbauen

### Phase 2: Aufwärmen (Tag 1-3)

**Was passiert:**

- Member lässt Community auf sich wirken
- Konsumiert Content
- Baut Vertrauen auf

### Phase 3: Goodwill Call (Tag 3+)

**Was passiert:**

- Anruf vom Setter (über CRM)
- Lockeres Gespräch mit Mehrwert
- Qualifizierung + Terminbuchung

### Parallel: Chat Setting

- Anschreiben über Skool
- Setting-Prozess
- Terminbuchung

**Das ist der komplette Prozess.**

## Phase 1: Willkommen – Der erste Eindruck

### Schritt 1: Automatische Willkommensnachricht

**Die Einstellung:**

In Skool-Einstellungen:

- "Automatische Nachricht bei Beitritt"
- Aktivieren
- Nachricht verfassen

**Die perfekte Willkommensnachricht:**

"Hey [Name]! 👋

Willkommen in der [Community-Name]!

Ich bin [Coach-Name] und freue mich riesig, dass Du dabei bist.

Hier ein paar erste Schritte:

1️⃣ Stell Dich kurz im Intro-Thread vor  
2️⃣ Schau Dir die Ressourcen im Classroom an  
3️⃣ Stelle Fragen – wir helfen gerne!

Und wenn Du möchtest, können wir auch gerne ein 1:1 Gespräch führen, wo ich Dir zeige, wie Du [Ziel] am schnellsten erreichst.

Einfach hier Bescheid geben! 🚀

Viel Erfolg!  
[Coach-Name]"

**Wichtige Elemente:**

- ✅ Persönlich (Name)
- ✅ Warm und freundlich
- ✅ Klare nächste Schritte
- ✅ Soft CTA (Gespräch anbieten)
- ✅ Kein harter Pitch

### Schritt 2: Setter nimmt Kontakt auf

**Timing:** Innerhalb von 24 Stunden

**Wer:** Setter (als Moderator vorgestellt)

**Die Nachricht:**

"Hey [Name]! 👋

Ich bin [Setter-Name], einer der Moderatoren hier in der Community.

Habe gesehen, dass Du gerade beigetreten bist – willkommen! 🎉

Falls Du Fragen hast oder Unterstützung brauchst, melde Dich gerne jederzeit bei mir.

Ich bin hier, um zu helfen! 💪

Viel Erfolg!  
[Setter-Name]"

**Der Zweck:**

- Beziehung aufbauen
- Vertrauen schaffen
- Ansprechpartner sein
- Nicht verkaufen (noch nicht)

### Schritt 3: CRM-Integration

**Wichtig:** Alles muss im CRM getrackt werden.

**Die Integration:**

- **Neues Mitglied tritt bei** → Automatisch im CRM angelegt
- **Willkommensnachricht gesendet** → Im CRM vermerkt
- **Setter-Kontakt hergestellt** → Im CRM vermerkt
- **Phase: "Neu – Aufwärmen"** → Im CRM gesetzt

**Warum wichtig?**

- Übersicht behalten
- Nichts vergessen
- Systematisch arbeiten
- Skalierbar

## Phase 2: Aufwärmen – Vertrauen aufbauen

### Die 3-Tage-Regel

**Warum 3 Tage?**

- Member braucht Zeit
- Community auf sich wirken lassen
- Content konsumieren
- Vertrauen aufbauen

**Was in diesen 3 Tagen passiert:**

**Tag 1:**

- Member schaut sich um
- Liest Posts
- Sieht Testimonials
- Konsumiert Content

**Tag 2:**

- Member wird aktiver
- Stellt vielleicht Fragen
- Interagiert
- Baut Beziehung auf

**Tag 3:**

- Member ist "warm"
- Kennt Community
- Hat Vertrauen
- Bereit für Gespräch

### Wichtig: Selbstbuchungs-Möglichkeit

**Der Post in der Community:**

**Titel:** "🎯 Kostenloses Strategiegespräch buchen"

**Inhalt:**

"Du willst [Ziel] erreichen?

Buche Dir hier ein kostenloses 1:1 Strategiegespräch mit mir:

👉 [Calendly-Link]

**In diesem Gespräch:**  
✅ Analysieren wir Deine aktuelle Situation  
✅ Erstellen wir einen individuellen Plan  
✅ Zeige ich Dir, wie Du [Ziel] erreichst

[Testimonials von anderen Members]

Buche jetzt: [Link]"

**Warum wichtig?**

- Manche buchen selbst
- Weniger Arbeit für Setter
- Zeigt Interesse
- Qualifiziert sich selbst

### Die Balance: Wert vs. Verkauf

**Das Problem:**

- Zu viel Verkauf = Leute verlassen Community
- Zu wenig Verkauf = Kein Umsatz

**Die Lösung:**

**80/20-Regel:**

- 80% Wert geben (Content, Hilfe, Support)
- 20% Verkauf (Gespräche anbieten, Programm erwähnen)

**Praktisch:**

- 4 wertvolle Posts
- 1 Post mit CTA
- Wiederholen

So bleibt die Balance.

## Phase 3: Goodwill Call – Der Verkauf

### Was ist ein Goodwill Call?

**Definition:**

Ein Anruf, der:

- Locker und freundlich ist
- Mehrwert gibt
- Nicht direkt verkauft
- Aber qualifiziert

**Der Unterschied zu normalem Setting:**

**Normales Setting:**

- Direkt ins Qualifizieren
- Fokus auf Verkauf
- Kann aufdringlich wirken

**Goodwill Call:**

- Lockerer Einstieg
- Offene Fragen
- Insights geben
- Dann qualifizieren

### Der perfekte Goodwill Call

**Die Struktur:**

**1. Lockerer Einstieg (2-3 Min.)**

"Hey [Name]! Wie geht's Dir?

Ich habe gesehen, Du bist seit ein paar Tagen in der Community – wie gefällt's Dir bisher?"

**2. Offene Fragen (3-5 Min.)**

"Erzähl mal: Was ist denn aktuell Deine größte Herausforderung bei [Thema]?"

"Wo stehst Du gerade? Was hast Du schon versucht?"

**3. Insights geben (2-3 Min.)**

"Okay, verstehe. Weißt Du, was ich bei vielen sehe, die in Deiner Situation sind? [Insight]"

"Hast Du schon mal über [Strategie] nachgedacht?"

**4. Qualifizieren (3-5 Min.)**

"Wie wichtig ist es Dir, [Ziel] zu erreichen?"

"Was würde es für Dich bedeuten, wenn Du [Ziel] erreichst?"

"Was hält Dich aktuell davon ab?"

**5. Soft Close (2-3 Min.)**

"Weißt Du was? Ich glaube, ich kann Dir da wirklich helfen.

Lass uns doch mal ein ausführliches Gespräch führen, wo wir uns Deine Situation im Detail anschauen und einen konkreten Plan erstellen.

Passt Dir [Datum/Uhrzeit]?"

**Gesamtdauer:** 15-20 Minuten

### Die Automatisierung

**Im CRM:**

**Tag 3 nach Beitritt:**

- Automatische Aufgabe für Setter
- "Goodwill Call mit [Name]"
- Priorität: Hoch

**Der Setter:**

- Sieht Aufgabe im CRM
- Ruft Member an
- Führt Goodwill Call
- Bucht Termin oder setzt Follow-up

**Wenn nicht erreicht:**

- Automatisch neue Aufgabe in 6 Stunden
- Maximal 3 Versuche pro Tag
- Nach 3 Tagen: Chat Setting

## Chat Setting: Der parallele Prozess

### Wann Chat Setting?

**Parallel zum Goodwill Call:**

- Tag 3: Goodwill Call versuchen
- Wenn nicht erreicht: Chat Setting

**Oder:**

- Member antwortet nicht auf Anruf
- Member bevorzugt Chat
- Member ist schwer erreichbar

### Die perfekte Chat-Setting-Nachricht

**Die Struktur:**

**Nachricht 1: Einstieg**

"Hey [Name]! 👋

Wie läuft's? Hast Du Dich schon ein bisschen in der Community umgeschaut?"

**Nachricht 2: Wert geben**

"Ich habe übrigens gesehen, dass Du Dich für [Thema] interessierst.

Dazu habe ich gerade einen neuen Post gemacht, könnte für Dich interessant sein: [Link]"

**Nachricht 3: Qualifizieren**

"Btw: Wo stehst Du denn aktuell bei [Thema]? Was ist Deine größte Herausforderung?"

**Nachricht 4: Soft Close**

"Weißt Du was? Lass uns doch mal telefonieren.

Ich kann Dir da ein paar Tipps geben, die Dir wirklich weiterhelfen.

Passt Dir [Datum/Uhrzeit]?"

**Timing:**

- Nachricht 1: Sofort
- Nachricht 2: Nach Antwort
- Nachricht 3: Nach Antwort
- Nachricht 4: Nach Antwort

**Nicht alle auf einmal!**

## Die häufigsten Fehler

### Fehler #1: Zu schnell pitchen

"Hey, willkommen! Willst Du kaufen?"

**Falsch.** Erst Beziehung aufbauen.

### Fehler #2: Zu lange warten

"Ich warte mal 2 Wochen, bevor ich ihn anschreibe."

**Falsch.** Nach 3 Tagen ist optimal.

### Fehler #3: Zu viele Nachrichten

"Ich schicke ihm 10 Nachrichten am Tag."

**Falsch.** Nicht nerven, Geduld haben.

### Fehler #4: Kein System

"Ich schreibe, wann ich Lust habe."

**Falsch.** Systematisch arbeiten.

## Die CRM-Integration: Der Schlüssel zum Erfolg

### Warum CRM-Integration essentiell ist

**Das Problem ohne CRM:**

- Übersicht verlieren
- Vergessen, wen anzurufen
- Keine Systematik
- Nicht skalierbar

**Die Lösung mit CRM:**

- Perfekte Übersicht
- Automatische Aufgaben
- Systematischer Prozess
- Perfekt skalierbar

### Die perfekte Integration

**Schritt 1: Skool → CRM**

- Neues Mitglied tritt bei
- Automatisch im CRM angelegt
- Alle Daten übertragen

**Schritt 2: Phasen-Tracking**

- Phase 1: Neu (Tag 0)
- Phase 2: Aufwärmen (Tag 1-3)
- Phase 3: Goodwill Call (Tag 3+)
- Phase 4: Termin gebucht
- Phase 5: Kunde

**Schritt 3: Automatische Aufgaben**

- Tag 0: Setter-Kontakt
- Tag 3: Goodwill Call
- Tag 4: Chat Setting (wenn nicht erreicht)
- Tag 7: Follow-up

**Schritt 4: Tracking**

- Alle Interaktionen getrackt
- Alle Gespräche protokolliert
- Alle Termine gespeichert
- Perfekte Übersicht

## Praxisbeispiel: Der komplette Prozess

**Ausgangssituation:**

- Online-Coaching-Business
- Skool-Community mit 500 Members
- Kein systematischer Prozess
- Conversion: 2%

**Vorher:**

- Chaotisch
- Manuell
- Keine Übersicht
- Wenig Umsatz

**Nachher (mit System):**

**Tag 0:**

- Max tritt Community bei
- Automatische Willkommensnachricht
- Setter schreibt ihm
- Im CRM angelegt

**Tag 1-3:**

- Max schaut sich um
- Liest Posts
- Sieht Testimonials
- Baut Vertrauen auf

**Tag 3:**

- Setter ruft Max an (Goodwill Call)
- Lockeres Gespräch
- Max erzählt von Herausforderungen
- Setter gibt Insights
- Termin wird gebucht

**Tag 7:**

- Closing Call
- Max kauft Programm (€3.000)

**Ergebnis:**

- Conversion: 2% → 12%
- Umsatz: +500%
- Systematisch
- Skalierbar

## Die häufigsten Fehler vermeiden

### Fehler #1: Zu aggressiv verkaufen

**Problem:**

"Hey, willkommen! Kauf jetzt mein Programm für €5.000!"

**Lösung:**

Erst Beziehung aufbauen, dann verkaufen.

### Fehler #2: Kein System

**Problem:**

"Ich schreibe Leute an, wenn ich dran denke."

**Lösung:**

Systematischer Prozess mit CRM.

### Fehler #3: Keine Balance

**Problem:**

"Ich poste nur Verkaufs-Posts."

**Lösung:**

80% Wert, 20% Verkauf.

### Fehler #4: Keine Automatisierung

**Problem:**

"Ich mache alles manuell."

**Lösung:**

CRM-Integration mit Automatisierung.

## Zusammenfassung: Der 3-Phasen-Prozess

### Phase 1: Willkommen (Tag 0)

- ✅ Automatische Willkommensnachricht
- ✅ Setter-Kontakt innerhalb 24h
- ✅ CRM-Integration

### Phase 2: Aufwärmen (Tag 1-3)

- ✅ Member lässt Community wirken
- ✅ Selbstbuchungs-Möglichkeit
- ✅ 80/20 Wert vs. Verkauf

### Phase 3: Goodwill Call (Tag 3+)

- ✅ Lockerer Anruf mit Mehrwert
- ✅ Qualifizierung
- ✅ Terminbuchung

### Parallel: Chat Setting

- ✅ Wenn nicht telefonisch erreicht
- ✅ Systematischer Chat-Prozess
- ✅ Terminbuchung

### Die Ergebnisse:

- Conversion: 2% → 12%
- Umsatz: +500%
- Systematisch
- Skalierbar

## Bereit für systematischen Community-Vertrieb?

Der richtige Prozess macht den Unterschied zwischen 2% und 12% Conversion.

Die Frage ist: Willst Du weiterhin chaotisch arbeiten und Potenzial verschenken – oder willst Du ein System, das funktioniert?

**Buche jetzt Deine kostenlose Demo** und ich zeige Dir, wie wir diesen kompletten Skool-Community-Prozess für Dich implementieren – mit CRM-Integration, Automatisierung und allen bewährten Strategien.
  `,
  en: `
# From Free Skool Member to High-Ticket Customers: The Systematic Process

You have a Skool community with free members – but how do you systematically convert them into paying high-ticket customers?

In this article, I'll show you the exact 3-phase process with which you:

- Systematically warm up free members
- Sell without being pushy
- Win high-ticket customers
- Make everything automated and scalable

After dozens of Skool community setups, this is the proven process that works.

## The Problem with Skool Communities

### What Most People Do Wrong:

**Scenario 1: No Strategy**

- Build community
- Bring people in
- Hope they buy
- **Result:** Nobody buys

**Scenario 2: Too Aggressive**

- Build community
- Pitch immediately
- Constantly sell
- **Result:** People leave community

**Scenario 3: No System**

- Build community
- Manually message people
- Chaotic
- **Result:** Inefficient, not scalable

### The Solution: A Systematic Process

**What you need:**

- **Clear phases** – When does what happen?
- **Automation** – No manual chaos
- **CRM integration** – Everything tracked
- **Balance** – Give value + Sell

That's the key to success.

## The 3-Phase Process: Overview

### Phase 1: Welcome (Day 0)

**What happens:**

- Automatic welcome message from coach
- Initial contact from setter (within 24h)
- Build relationship

### Phase 2: Warm-up (Day 1-3)

**What happens:**

- Member lets community sink in
- Consumes content
- Builds trust

### Phase 3: Goodwill Call (Day 3+)

**What happens:**

- Call from setter (via CRM)
- Casual conversation with value
- Qualification + appointment booking

### Parallel: Chat Setting

- Message via Skool
- Setting process
- Appointment booking

**That's the complete process.**

## Phase 1: Welcome – The First Impression

### Step 1: Automatic Welcome Message

**The setting:**

In Skool settings:

- "Automatic message on join"
- Activate
- Write message

**The perfect welcome message:**

"Hey [Name]! 👋

Welcome to [Community-Name]!

I'm [Coach-Name] and I'm thrilled you're here.

Here are some first steps:

1️⃣ Introduce yourself briefly in the intro thread  
2️⃣ Check out the resources in the Classroom  
3️⃣ Ask questions – we're happy to help!

And if you'd like, we can also have a 1:1 conversation where I show you how to achieve [Goal] fastest.

Just let me know here! 🚀

Good luck!  
[Coach-Name]"

**Important elements:**

- ✅ Personal (name)
- ✅ Warm and friendly
- ✅ Clear next steps
- ✅ Soft CTA (offer conversation)
- ✅ No hard pitch

### Step 2: Setter Makes Contact

**Timing:** Within 24 hours

**Who:** Setter (introduced as moderator)

**The message:**

"Hey [Name]! 👋

I'm [Setter-Name], one of the moderators here in the community.

Saw you just joined – welcome! 🎉

If you have questions or need support, feel free to reach out anytime.

I'm here to help! 💪

Good luck!  
[Setter-Name]"

**The purpose:**

- Build relationship
- Create trust
- Be contact person
- Don't sell (not yet)

### Step 3: CRM Integration

**Important:** Everything must be tracked in CRM.

**The integration:**

- **New member joins** → Automatically created in CRM
- **Welcome message sent** → Noted in CRM
- **Setter contact established** → Noted in CRM
- **Phase: "New – Warming up"** → Set in CRM

**Why important?**

- Keep overview
- Don't forget anything
- Work systematically
- Scalable

## Phase 2: Warm-up – Build Trust

### The 3-Day Rule

**Why 3 days?**

- Member needs time
- Let community sink in
- Consume content
- Build trust

**What happens in these 3 days:**

**Day 1:**

- Member looks around
- Reads posts
- Sees testimonials
- Consumes content

**Day 2:**

- Member becomes more active
- Maybe asks questions
- Interacts
- Builds relationship

**Day 3:**

- Member is "warm"
- Knows community
- Has trust
- Ready for conversation

### Important: Self-Booking Option

**The post in the community:**

**Title:** "🎯 Book Free Strategy Call"

**Content:**

"Want to achieve [Goal]?

Book a free 1:1 strategy call with me here:

👉 [Calendly-Link]

**In this call:**  
✅ We analyze your current situation  
✅ We create an individual plan  
✅ I show you how to achieve [Goal]

[Testimonials from other members]

Book now: [Link]"

**Why important?**

- Some book themselves
- Less work for setter
- Shows interest
- Self-qualifies

### The Balance: Value vs. Sales

**The problem:**

- Too much sales = People leave community
- Too little sales = No revenue

**The solution:**

**80/20 rule:**

- 80% give value (content, help, support)
- 20% sales (offer conversations, mention program)

**Practically:**

- 4 valuable posts
- 1 post with CTA
- Repeat

That maintains the balance.

## Phase 3: Goodwill Call – The Sale

### What is a Goodwill Call?

**Definition:**

A call that:

- Is casual and friendly
- Gives value
- Doesn't directly sell
- But qualifies

**The difference from normal setting:**

**Normal setting:**

- Directly into qualifying
- Focus on sales
- Can seem pushy

**Goodwill call:**

- Casual entry
- Open questions
- Give insights
- Then qualify

### The Perfect Goodwill Call

**The structure:**

**1. Casual Entry (2-3 min.)**

"Hey [Name]! How are you?

I saw you've been in the community for a few days – how do you like it so far?"

**2. Open Questions (3-5 min.)**

"Tell me: What's your biggest challenge with [Topic] right now?"

"Where are you currently? What have you already tried?"

**3. Give Insights (2-3 min.)**

"Okay, I understand. You know what I see with many people in your situation? [Insight]"

"Have you ever thought about [Strategy]?"

**4. Qualify (3-5 min.)**

"How important is it to you to achieve [Goal]?"

"What would it mean for you if you achieve [Goal]?"

"What's currently holding you back?"

**5. Soft Close (2-3 min.)**

"You know what? I think I can really help you there.

Let's have a detailed conversation where we look at your situation in detail and create a concrete plan.

Does [Date/Time] work for you?"

**Total duration:** 15-20 minutes

### The Automation

**In CRM:**

**Day 3 after joining:**

- Automatic task for setter
- "Goodwill call with [Name]"
- Priority: High

**The setter:**

- Sees task in CRM
- Calls member
- Conducts goodwill call
- Books appointment or sets follow-up

**If not reached:**

- Automatically new task in 6 hours
- Maximum 3 attempts per day
- After 3 days: Chat setting

## Chat Setting: The Parallel Process

### When Chat Setting?

**Parallel to goodwill call:**

- Day 3: Try goodwill call
- If not reached: Chat setting

**Or:**

- Member doesn't answer call
- Member prefers chat
- Member is hard to reach

### The Perfect Chat Setting Message

**The structure:**

**Message 1: Entry**

"Hey [Name]! 👋

How's it going? Have you looked around the community a bit?"

**Message 2: Give Value**

"By the way, I saw you're interested in [Topic].

I just made a new post about it, might be interesting for you: [Link]"

**Message 3: Qualify**

"Btw: Where are you currently with [Topic]? What's your biggest challenge?"

**Message 4: Soft Close**

"You know what? Let's talk on the phone.

I can give you some tips that will really help you.

Does [Date/Time] work for you?"

**Timing:**

- Message 1: Immediately
- Message 2: After response
- Message 3: After response
- Message 4: After response

**Not all at once!**

## The Most Common Mistakes

### Mistake #1: Pitch Too Fast

"Hey, welcome! Want to buy?"

**Wrong.** Build relationship first.

### Mistake #2: Wait Too Long

"I'll wait 2 weeks before messaging them."

**Wrong.** After 3 days is optimal.

### Mistake #3: Too Many Messages

"I'll send them 10 messages a day."

**Wrong.** Don't annoy, have patience.

### Mistake #4: No System

"I message when I feel like it."

**Wrong.** Work systematically.

## CRM Integration: The Key to Success

### Why CRM Integration is Essential

**The problem without CRM:**

- Lose overview
- Forget who to call
- No system
- Not scalable

**The solution with CRM:**

- Perfect overview
- Automatic tasks
- Systematic process
- Perfectly scalable

### The Perfect Integration

**Step 1: Skool → CRM**

- New member joins
- Automatically created in CRM
- All data transferred

**Step 2: Phase Tracking**

- Phase 1: New (Day 0)
- Phase 2: Warming up (Day 1-3)
- Phase 3: Goodwill call (Day 3+)
- Phase 4: Appointment booked
- Phase 5: Customer

**Step 3: Automatic Tasks**

- Day 0: Setter contact
- Day 3: Goodwill call
- Day 4: Chat setting (if not reached)
- Day 7: Follow-up

**Step 4: Tracking**

- All interactions tracked
- All conversations logged
- All appointments saved
- Perfect overview

## Practical Example: The Complete Process

**Initial situation:**

- Online coaching business
- Skool community with 500 members
- No systematic process
- Conversion: 2%

**Before:**

- Chaotic
- Manual
- No overview
- Little revenue

**After (with system):**

**Day 0:**

- Max joins community
- Automatic welcome message
- Setter messages him
- Created in CRM

**Day 1-3:**

- Max looks around
- Reads posts
- Sees testimonials
- Builds trust

**Day 3:**

- Setter calls Max (goodwill call)
- Casual conversation
- Max talks about challenges
- Setter gives insights
- Appointment booked

**Day 7:**

- Closing call
- Max buys program (€3,000)

**Result:**

- Conversion: 2% → 12%
- Revenue: +500%
- Systematic
- Scalable

## Avoid the Most Common Mistakes

### Mistake #1: Sell Too Aggressively

**Problem:**

"Hey, welcome! Buy my program for €5,000 now!"

**Solution:**

Build relationship first, then sell.

### Mistake #2: No System

**Problem:**

"I message people when I think of it."

**Solution:**

Systematic process with CRM.

### Mistake #3: No Balance

**Problem:**

"I only post sales posts."

**Solution:**

80% value, 20% sales.

### Mistake #4: No Automation

**Problem:**

"I do everything manually."

**Solution:**

CRM integration with automation.

## Summary: The 3-Phase Process

### Phase 1: Welcome (Day 0)

- ✅ Automatic welcome message
- ✅ Setter contact within 24h
- ✅ CRM integration

### Phase 2: Warm-up (Day 1-3)

- ✅ Member lets community sink in
- ✅ Self-booking option
- ✅ 80/20 value vs. sales

### Phase 3: Goodwill Call (Day 3+)

- ✅ Casual call with value
- ✅ Qualification
- ✅ Appointment booking

### Parallel: Chat Setting

- ✅ If not reached by phone
- ✅ Systematic chat process
- ✅ Appointment booking

### The Results:

- Conversion: 2% → 12%
- Revenue: +500%
- Systematic
- Scalable

## Ready for Systematic Community Sales?

The right process makes the difference between 2% and 12% conversion.

The question is: Do you want to continue working chaotically and wasting potential – or do you want a system that works?

**Book your free demo now** and I'll show you how we implement this complete Skool community process for you – with CRM integration, automation, and all proven strategies.
  `,
}
